import type { ComboboxItem } from '@/components'
import { Combobox, Field, FieldLabel } from '@/components'
import { Checkbox } from '@/components_deprecated'
import { Table } from '@/components_deprecated/Table'
import { Tooltip } from '@/components/tooltip'
import type {
  IssueFormFieldCategoriesQuery,
  IssueFormFieldValue,
  IssueFormSectionValue,
  IssueRelationConfigsQuery,
  SetIssueCategoryFieldConfigsInput,
  SetIssueFieldConfigsInput,
  SetIssueRelationCategoryConfigsInput,
} from '@/modules/issues'
import {
  ISSUE_FORM_FIELD_CATEGORIES_QUERY,
  ISSUE_RELATION_CONFIGS_QUERY,
} from '@/modules/issues'
import { IssueFormSection, useIssueFieldConfig } from '@/modules/issues'
import { useQuery } from '@apollo/client'
import { Button, Icon, useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useMemo, useState } from 'react'
import { OldSection, SectionHeader } from '../common'
import { useIssueFieldConfigMutations } from '../../util/mutations.hooks'

/** The four booleans a customer can flip per field. */
const FLAGS = [
  'visibleOnCreate',
  'requiredOnCreate',
  'visibleOnEdit',
  'requiredOnEdit',
] as const

type Flag = (typeof FLAGS)[number]

type Overrides = Record<string, Partial<Record<Flag, boolean>>>

type GridSection = IssueFormSectionValue | 'RELATIONS'

const SECTION_ORDER: GridSection[] = [
  IssueFormSection.META,
  IssueFormSection.LOCATION,
  IssueFormSection.EVENT,
  IssueFormSection.RESOLUTION,
  IssueFormSection.COST,
  // Company-defined relations come last: they vary per tenant, so the
  // fixed fields stay in a stable place in the grid.
  'RELATIONS',
]

/**
 * Company-defined relations are shown in the same grid as the built-in
 * fields, but they live on IssueRelation rather than IssueFieldConfig, so
 * they are saved through a different mutation. This prefix keeps the two
 * apart in the draft state without a second parallel structure.
 */
const RELATION_PREFIX = 'relation:'

/** The picker value standing for the company-wide configuration. */
const STANDARD = ''

const CATEGORY_PICKER_ID = 'issue-form-fields-category'

/** One row of the grid, whichever model backs it. */
interface GridRow {
  key: string
  label: string
  section: GridSection
  canHide: boolean
  canRequire: boolean
  /** True when this category, rather than the standard, supplied the flags. */
  overridden: boolean
  visibleOnCreate: boolean
  requiredOnCreate: boolean
  visibleOnEdit: boolean
  requiredOnEdit: boolean
}

/** Requiring a row implies showing it — mirrors the model's clean(). */
function applyFlag(
  row: GridRow,
  current: Partial<Record<Flag, boolean>>,
  flag: Flag,
  value: boolean
): Partial<Record<Flag, boolean>> {
  const next = { ...current, [flag]: value }

  if (flag === 'requiredOnCreate' && value) next.visibleOnCreate = true
  if (flag === 'requiredOnEdit' && value) next.visibleOnEdit = true
  if (flag === 'visibleOnCreate' && !value) next.requiredOnCreate = false
  if (flag === 'visibleOnEdit' && !value) next.requiredOnEdit = false

  if (!row.canHide) {
    next.visibleOnCreate = true
    next.visibleOnEdit = true
  }
  if (!row.canRequire) {
    next.requiredOnCreate = false
    next.requiredOnEdit = false
  }

  return next
}

export const IssueFormFieldsSection: React.FC = () => {
  const translations = useTranslate({
    header: 'settings.issue-form-fields',
    info: 'settings.info.issue-form-fields',
    field: 'settings.issue-form-field',
    visibleOnCreate: 'settings.visible-on-create',
    requiredOnCreate: 'settings.required-on-create',
    visibleOnEdit: 'settings.visible-on-edit',
    requiredOnEdit: 'settings.required-on-edit',
    alwaysVisible: 'settings.issue-form-field-always-visible',
    cannotRequire: 'settings.issue-form-field-cannot-require',
    saved: 'settings.issue-form-fields-saved',
    save: 'common.save',
    cancel: 'common.cancel',
    appliesTo: 'settings.issue-form-fields-applies-to',
    searchCategory: 'settings.issue-form-fields-search-category',
    standard: 'settings.issue-form-fields-standard',
    configured: 'settings.issue-form-fields-configured',
    categoryInfo: 'settings.info.issue-form-fields-category',
    overridden: 'settings.issue-form-field-overridden',
    resetField: 'settings.issue-form-field-reset',
    resetCategory: 'settings.issue-form-fields-reset-category',
    resetCategoryDone: 'settings.issue-form-fields-reset-category-done',
    sections: {
      META: 'settings.issue-form-section-meta',
      LOCATION: 'settings.issue-form-section-location',
      EVENT: 'settings.issue-form-section-event',
      RESOLUTION: 'settings.issue-form-section-resolution',
      COST: 'settings.issue-form-section-cost',
      RELATIONS: 'settings.issue-form-section-relations',
    },
    // Reuses the issue form's own keys, so the settings grid and the form
    // cannot drift apart in wording. Keys are the IssueFormField values.
    labels: {
      title: 'issues.title',
      category: 'common.category',
      subcategory: 'common.sub-category',
      severity: 'common.severity',
      risk_aspect: 'common.risk-aspect',
      process_choice: 'issues.process',
      responsible_user: 'common.responsible',
      case_handler: 'common.case-handler',
      tags: 'settings.issue-tags',
      time_of_issue: 'common.date',
      location_placename: 'common.location',
      location_description: 'issues.location-description',
      vehicle_plate_number: 'issues.equipment-material',
      issue_description: 'common.description',
      damage_description: 'issues.damage-description',
      reason: 'issues.reason',
      immediate_action: 'issues.immediate-action',
      action: 'issues.action',
      solution_suggestion: 'issues.suggested-solution',
      cost_estimation: 'issues.cost-estimation',
      attachments: 'common.attachments',
    },
  })

  const addToast = useToast()
  const [category, setCategory] = useState<string>(STANDARD)
  const inCategoryMode = category !== STANDARD

  const { configs, loading } = useIssueFieldConfig(category || null)
  const { data: categoryData } = useQuery<IssueFormFieldCategoriesQuery>(
    ISSUE_FORM_FIELD_CATEGORIES_QUERY
  )
  const { data: relationData } = useQuery<IssueRelationConfigsQuery>(
    ISSUE_RELATION_CONFIGS_QUERY,
    { variables: { category: category || null } }
  )
  const mutations = useIssueFieldConfigMutations()

  const [overrides, setOverrides] = useState<Overrides>({})

  const categories = useMemo(
    () => (categoryData?.allIssueCategories.edges ?? []).map(edge => edge.node),
    [categoryData]
  )

  const categoryItems: ComboboxItem<string>[] = useMemo(
    () => [
      { value: STANDARD, label: translations.standard },
      ...categories.map(node => ({
        value: node.id,
        // The badge answers "which categories deviate from the standard"
        // without opening each one.
        label: node.hasFieldConfigs ? (
          <span className="flex items-center gap-2">
            {node.name}
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
              {translations.configured}
            </span>
          </span>
        ) : (
          node.name
        ),
        // A badged label is a node, so the search needs the plain name.
        filterValue: node.name,
      })),
    ],
    [categories, translations.configured, translations.standard]
  )

  const selectedHasConfigs = categories.some(
    node => node.id === category && node.hasFieldConfigs
  )

  const rows: GridRow[] = useMemo(() => {
    const fieldRows = configs.map(config => ({
      key: config.field,
      label: translations.labels[config.field],
      section: config.section,
      canHide: config.canHide,
      canRequire: config.canRequire,
      overridden: config.overridden,
      visibleOnCreate: config.visibleOnCreate,
      requiredOnCreate: config.requiredOnCreate,
      visibleOnEdit: config.visibleOnEdit,
      requiredOnEdit: config.requiredOnEdit,
    }))

    const relationRows = (relationData?.issueRelationConfigs ?? []).map(
      config => ({
        key: RELATION_PREFIX + config.relation,
        label: config.name,
        section: 'RELATIONS' as const,
        canHide: true,
        canRequire: true,
        overridden: config.overridden,
        visibleOnCreate: config.visibleOnCreate,
        requiredOnCreate: config.requiredOnCreate,
        visibleOnEdit: config.visibleOnEdit,
        requiredOnEdit: config.requiredOnEdit,
      })
    )

    return [...fieldRows, ...relationRows]
  }, [configs, relationData, translations.labels])

  const grouped = useMemo(() => {
    const bySection = new Map<GridSection, GridRow[]>()
    for (const row of rows) {
      const list = bySection.get(row.section) ?? []
      list.push(row)
      bySection.set(row.section, list)
    }
    return bySection
  }, [rows])

  const dirty = Object.keys(overrides).length > 0

  function valueFor(row: GridRow, flag: Flag): boolean {
    return overrides[row.key]?.[flag] ?? row[flag]
  }

  function isLocked(row: GridRow, flag: Flag): boolean {
    return flag.startsWith('visible') ? !row.canHide : !row.canRequire
  }

  function handleSelectCategory(value: string) {
    setOverrides({})
    setCategory(value)
  }

  function handleToggle(row: GridRow, flag: Flag, value: boolean) {
    setOverrides(current => {
      const merged = applyFlag(row, current[row.key] ?? {}, flag, value)

      // Drop the entry entirely when it matches the saved state again, so the
      // Save button reflects whether anything actually changed.
      const changed = FLAGS.some(
        candidate =>
          merged[candidate] !== undefined &&
          merged[candidate] !== row[candidate]
      )
      const next = { ...current }
      if (changed) next[row.key] = merged
      else delete next[row.key]
      return next
    })
  }

  async function handleResetField(row: GridRow) {
    const isRelation = row.key.startsWith(RELATION_PREFIX)
    const result = isRelation
      ? await mutations.deleteRelationCategoryConfigs({
          variables: {
            category,
            relations: [row.key.slice(RELATION_PREFIX.length)],
          },
        })
      : await mutations.deleteCategoryConfigs({
          variables: { category, fields: [row.key] },
        })
    if (!result.data) return

    setOverrides(current => {
      const next = { ...current }
      delete next[row.key]
      return next
    })
  }

  async function handleResetCategory() {
    // Both models can hold overrides for a category, so a reset clears both.
    const results = await Promise.all([
      mutations.deleteCategoryConfigs({
        variables: { category, fields: null },
      }),
      mutations.deleteRelationCategoryConfigs({
        variables: { category, relations: null },
      }),
    ])
    if (results.some(result => !result.data)) return

    setOverrides({})
    addToast('success', translations.resetCategoryDone)
  }

  async function handleSave() {
    const entries = Object.entries(overrides)
    if (entries.length === 0) return

    const fieldEntries = entries.filter(
      ([key]) => !key.startsWith(RELATION_PREFIX)
    )
    const relationEntries = entries.filter(([key]) =>
      key.startsWith(RELATION_PREFIX)
    )

    if (inCategoryMode) {
      const fieldConfigs: SetIssueCategoryFieldConfigsInput[] =
        fieldEntries.map(([field, flags]) => ({
          field: field as IssueFormFieldValue,
          ...flags,
        }))
      const relationConfigs: SetIssueRelationCategoryConfigsInput[] =
        relationEntries.map(([key, flags]) => ({
          relation: key.slice(RELATION_PREFIX.length),
          ...flags,
        }))

      // Both models can carry a category override, so a save can be two round
      // trips. Run them together and only confirm if every one succeeded.
      const results = await Promise.all([
        fieldConfigs.length
          ? mutations.setCategoryConfigs({
              variables: { category, configs: fieldConfigs },
            })
          : null,
        relationConfigs.length
          ? mutations.setRelationCategoryConfigs({
              variables: { category, configs: relationConfigs },
            })
          : null,
      ])

      if (results.some(result => result && !result.data)) return

      setOverrides({})
      addToast('success', translations.saved)
      return
    }

    const fieldPayload: SetIssueFieldConfigsInput[] = fieldEntries.map(
      ([field, flags]) => ({ field: field as IssueFormFieldValue, ...flags })
    )

    // Two models back this grid, so a save can be two round trips. Run them
    // together and only confirm if every one succeeded.
    const results = await Promise.all([
      fieldPayload.length
        ? mutations.setConfigs({ variables: { configs: fieldPayload } })
        : null,
      ...relationEntries.map(([key, flags]) =>
        mutations.patchRelation({
          variables: { id: key.slice(RELATION_PREFIX.length), input: flags },
        })
      ),
    ])

    if (results.some(result => result && !result.data)) return
    if (fieldPayload.length && !results[0]?.data?.setIssueFieldConfigs?.ok)
      return

    setOverrides({})
    addToast('success', translations.saved)
  }

  return (
    <OldSection>
      <SectionHeader>{translations.header}</SectionHeader>
      <p className="mb-4 max-w-3xl text-sm text-stone-500">
        {inCategoryMode ? translations.categoryInfo : translations.info}
      </p>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field className="w-72">
          <FieldLabel htmlFor={CATEGORY_PICKER_ID}>
            {translations.appliesTo}
          </FieldLabel>
          <Combobox
            id={CATEGORY_PICKER_ID}
            mode="single"
            value={category}
            items={categoryItems}
            searchPlaceholder={translations.searchCategory}
            onChange={handleSelectCategory}
          />
        </Field>
        {inCategoryMode && (
          <Button
            disabled={!selectedHasConfigs || mutations.loading}
            background="gray8"
            onClick={handleResetCategory}
          >
            {translations.resetCategory}
          </Button>
        )}
      </div>

      <div className="overflow-x-auto">
        <Table>
          <thead>
            <tr>
              <th>{translations.field}</th>
              <th>{translations.visibleOnCreate}</th>
              <th>{translations.requiredOnCreate}</th>
              <th>{translations.visibleOnEdit}</th>
              <th>{translations.requiredOnEdit}</th>
              {inCategoryMode && <th />}
            </tr>
          </thead>
          <tbody>
            {SECTION_ORDER.flatMap(section => {
              const sectionRows = grouped.get(section) ?? []
              if (sectionRows.length === 0) return []

              return [
                <tr key={`section-${section}`}>
                  <td
                    colSpan={inCategoryMode ? 6 : 5}
                    className="bg-stone-50 text-xs font-semibold uppercase tracking-wide text-stone-500"
                  >
                    {translations.sections[section]}
                  </td>
                </tr>,
                ...sectionRows.map(row => (
                  <tr key={row.key}>
                    <td>
                      {row.label}
                      {inCategoryMode && row.overridden && (
                        <Tooltip content={translations.overridden}>
                          <span className="ml-2 inline-block h-2 w-2 rounded-full bg-amber-500 align-middle" />
                        </Tooltip>
                      )}
                    </td>
                    {FLAGS.map(flag => {
                      const locked = isLocked(row, flag)
                      const cell = (
                        <Checkbox
                          checked={valueFor(row, flag)}
                          disabled={locked}
                          onChange={value => handleToggle(row, flag, value)}
                        />
                      )

                      if (!locked) return <td key={flag}>{cell}</td>

                      const reason = flag.startsWith('visible')
                        ? translations.alwaysVisible
                        : translations.cannotRequire

                      return (
                        <td key={flag}>
                          <Tooltip content={reason}>
                            <span className="inline-flex">{cell}</span>
                          </Tooltip>
                        </td>
                      )
                    })}
                    {inCategoryMode && (
                      <td>
                        {row.overridden && (
                          <Tooltip content={translations.resetField}>
                            <button
                              type="button"
                              className="text-stone-400 hover:text-stone-700"
                              disabled={mutations.loading}
                              onClick={() => handleResetField(row)}
                            >
                              <Icon icon="rotate-left" />
                            </button>
                          </Tooltip>
                        )}
                      </td>
                    )}
                  </tr>
                )),
              ]
            })}
          </tbody>
        </Table>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button
          disabled={!dirty || mutations.loading}
          background="gray8"
          onClick={() => setOverrides({})}
        >
          {translations.cancel}
        </Button>
        <Button
          disabled={!dirty || mutations.loading || loading}
          onClick={handleSave}
        >
          {translations.save}
        </Button>
      </div>
    </OldSection>
  )
}
